import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { parsePaging, parseSorting } from '../../utils/queryString';
import useExtendedQuery from '../../hooks/useExtendedQuery';
import Spinner from '../Spinner';
import Paginator from '../Paginator';
import { ALL_MANUAL_CASH_LIMITS, ON_MANUAL_CASH_LIMIT_CHANGED } from '../../graphql/manualCashLimits';
import ManualCashLimitsList from './ManualCashLimitsList';
import Error from '../errors/Error';
import SubscriptionActions from '../../utils/SubscriptionActions';
import CreateManualCashLimitRow from './CreateManualCashLimitRow';
import useExtendedSubscription from '../../hooks/useExtendedSubscription';
import { showInfoNotification } from '../../actions/notifications';
import Constants from '../../utils/Constants';
import useAuthentication from '../../hooks/useAuthentication';
import { hasPrivilege } from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';

export function createNotificationText(t, action, memberName, memberId) {
    let notification;
    switch (action) {
        case SubscriptionActions.MANUAL_CASH_LIMIT_CREATED:
            notification = t('manual-cash-limits.notification.manual-cash-limit-create-thirdparty', { memberName, memberId });
            break;
        case SubscriptionActions.MANUAL_CASH_LIMIT_DELETED:
            notification = t('manual-cash-limits.notification.manual-cash-limit-delete-thirdparty', { memberName, memberId });
            break;
        default:
            notification = t('manual-cash-limits.notification.manual-cash-limits-modified-thirdparty');
    }
    return notification;
}

function ManualCashLimits() {
    const location = useLocation();
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const { page, perPage } = parsePaging(location.search);
    const {
        sortKey = 'member.memberId',
        sortDirection = Constants.SORT_ASC,
    } = parseSorting(location.search);
    const dispatch = useDispatch();
    const [refetchProps, setRefetchProps] = useState({
        perPage,
        page,
        sortKey,
        sortDirection,
    });
    const {
        loading,
        error,
        data,
        refetch,
    } = useExtendedQuery(ALL_MANUAL_CASH_LIMITS, {
        variables: refetchProps,
    });
    const onPageChange = (newPage) => setRefetchProps({
        ...refetchProps,
        page: newPage,
    });
    const onSort = (newSorting) => setRefetchProps({
        ...refetchProps,
        sortKey: newSorting.key,
        sortDirection: newSorting.direction,
    });
    useExtendedSubscription(ON_MANUAL_CASH_LIMIT_CHANGED, {
        refetch,
        refetchVariables: refetchProps,
        dataProperty: 'manualCashLimitChanged',
        onChangeCallback: ({ action, model: { member: { name, memberId } } }) => {
            if (hasPrivilege(userPrivileges, [Privileges.MANUAL_CASH_LIMIT_EDIT])) {
                const notification = createNotificationText(t, action, name, memberId);
                dispatch(showInfoNotification(notification));
            }
        },
    });
    let result;
    let manualCashLimitProps = null;
    if (loading) {
        result = <Spinner className="manual-cash-limits-loading" />;
    } else if (error) {
        result = <Error className="manual-cash-limits-error" error={error} />;
    } else {
        manualCashLimitProps = {
            availableMembers: data.allManualCashLimits.availableMembers,
            availableClearingHouses: data.allManualCashLimits.availableClearingHouses,
            availableCurrencies: data.allManualCashLimits.availableCurrencies,
            minCashLimit: data.allManualCashLimits.minCashLimit,
            maxCashLimit: data.allManualCashLimits.maxCashLimit,
        };
        result = (
            <Paginator
                totalCount={data.allManualCashLimits.manualCashLimitList.totalCount}
                perPage={perPage}
                currentPage={refetchProps.page}
                onPageChange={onPageChange}
                bottom
            >
                <ManualCashLimitsList
                    manualCashLimits={data.allManualCashLimits.manualCashLimitList.models}
                    onSort={onSort}
                    sortingParam={{ key: refetchProps.sortKey, direction: refetchProps.sortDirection }}
                />
            </Paginator>
        );
    }
    return (
        <>
            <h4>
                <span className="fa-layers fa-fw">
                    <FontAwesomeIcon icon="funnel-dollar" transform="shrink-8 up-3" />
                    <FontAwesomeIcon icon="hand-holding" />
                </span>
                &nbsp;{t('title.manual-cash-limits')}
            </h4>
            {manualCashLimitProps && <CreateManualCashLimitRow manualCashLimitProps={manualCashLimitProps} />}
            {result}
        </>
    );
}

export default ManualCashLimits;
